import type { ReactNode } from "react"

import { PageHeader } from "./page-header"
import { cn } from "../../lib/utils"

type AppShellProps = {
  title: string
  description?: string
  /** Optional glyph rendered immediately left of the title (e.g. a marketplace mark). */
  icon?: ReactNode
  /** When set, the title becomes a link that opens this URL in a new tab. */
  titleHref?: string
  actions?: ReactNode
  fullWidth?: boolean
  /** Lock the shell to the viewport height so the page owns its own scroll
   *  region(s) instead of the document scrolling. */
  fillViewport?: boolean
  /** Suppress the default page title when the page renders its own header. */
  hideTitle?: boolean
  /** Optional page-specific spacing on the shared main element. */
  contentClassName?: string
  children: ReactNode
}

export function AppShell({
  title,
  description,
  icon,
  titleHref,
  actions,
  fullWidth = false,
  fillViewport = false,
  hideTitle = false,
  children,
  contentClassName,
}: AppShellProps) {
  return (
    <div
      className={cn(
        "flex flex-col",
        fillViewport
          ? "h-[calc(100dvh-3.5rem)] overflow-hidden lg:h-screen"
          : "min-h-[calc(100dvh-3.5rem)] lg:min-h-screen"
      )}
    >
      <main
        className={cn(
          "flex-1",
          // Viewport-locked pages own their scroll region, so the generous
          // document-scroll padding would only eat workspace height.
          fillViewport
            ? "flex min-h-0 flex-col overflow-hidden py-4"
            : "py-6 pb-12 sm:py-8 sm:pb-14",
          contentLayoutClass(fullWidth),
          contentClassName
        )}
      >
        {!hideTitle && (
          <PageHeader
            title={title}
            {...(description ? { description } : {})}
            icon={icon}
            {...(titleHref ? { titleHref } : {})}
            actions={actions}
          />
        )}

        {children}
      </main>
    </div>
  )
}

// `container` is the shared page width: it brings its own centering + horizontal
// padding and the responsive 1440px cap. fullWidth opts out to go edge to edge
// (it keeps mx-auto/px-6 but drops the cap).
function contentLayoutClass(fullWidth: boolean): string {
  if (fullWidth) {
    return "mx-auto w-full px-6 max-w-none"
  }
  return "container"
}
